import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'

const stepLinkClassName =
	'inline-flex min-w-11 items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-body-sm font-medium transition-colors hover:bg-muted/70'

export function Pagination({
	currentPage,
	totalPages,
	basePath,
	query,
}: {
	currentPage: number
	totalPages: number
	basePath: string
	/** Search params every page link keeps, e.g. `{ q }` on the search page. */
	query?: Record<string, string>
}) {
	const { t } = useTranslation()

	function pageUrl(page: number) {
		const params = new URLSearchParams(query)
		if (page > 1) params.set('page', String(page))
		const search = params.toString()
		return search ? `${basePath}?${search}` : basePath
	}

	// Build visible page numbers (show max 7 with ellipsis)
	const pages: Array<number | 'ellipsis'> = []
	if (totalPages <= 7) {
		for (let i = 1; i <= totalPages; i++) pages.push(i)
	} else {
		pages.push(1)
		if (currentPage > 3) pages.push('ellipsis')
		for (
			let i = Math.max(2, currentPage - 1);
			i <= Math.min(totalPages - 1, currentPage + 1);
			i++
		) {
			pages.push(i)
		}
		if (currentPage < totalPages - 2) pages.push('ellipsis')
		pages.push(totalPages)
	}

	return (
		<nav
			aria-label={t('blog.pagination.page')}
			className="mt-16 flex flex-wrap items-center justify-center gap-1"
		>
			{currentPage > 1 && (
				<Link
					to={pageUrl(currentPage - 1)}
					aria-label={t('blog.pagination.previous')}
					className={stepLinkClassName}
				>
					<Icon name="arrow-left" aria-hidden="true" />
					<span className="hidden sm:inline">
						{t('blog.pagination.previous')}
					</span>
				</Link>
			)}

			{pages.map((page, i) =>
				page === 'ellipsis' ? (
					<span
						key={`ellipsis-${i}`}
						className="px-2 text-muted-foreground"
						aria-hidden="true"
					>
						&hellip;
					</span>
				) : (
					<Link
						key={page}
						to={pageUrl(page)}
						aria-current={page === currentPage ? 'page' : undefined}
						className={`text-body-sm min-w-11 rounded-xl px-3 py-2.5 text-center font-medium transition-colors ${
							page === currentPage
								? 'bg-primary/10 text-primary font-semibold'
								: 'hover:bg-muted/70'
						}`}
					>
						{page}
					</Link>
				),
			)}

			{currentPage < totalPages && (
				<Link
					to={pageUrl(currentPage + 1)}
					aria-label={t('blog.pagination.next')}
					className={stepLinkClassName}
				>
					<span className="hidden sm:inline">{t('blog.pagination.next')}</span>
					<Icon name="arrow-right" aria-hidden="true" />
				</Link>
			)}
		</nav>
	)
}
